import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { LoaderBar } from '@components/LoaderBar';
import { BoldText, MuteText, PageHeaderText } from '@elements/Common';
import { yupResolver } from '@hookform/resolvers/yup';
import { shippingReturnsRateHook } from '@hooks/shipping-label';
import { PrintLabelPopup } from '@page-components/order/PrintLabelPopup';
import { returnSchema } from '@page-components/order/common';
import { CommonUtility } from '@utility/common';
import {
  carrierServices,
  ErrorMessageConstant,
} from '@utility/constants/common';
import ShippingLabelService from '@utility/services/shipping-label';
import { ToastMessage } from '@utility/toast';
import { useAuth } from '@contexts/auth';
import { Calendar, Info, Package, Truck, User } from '@phosphor-icons/react';
import { Accordion, Button, cn, Popover } from '@skupreme/shared/ui';
import type { DropdownFormFieldProps } from 'types/common/form-fields';
import { ItemImage } from '@components/CatalogElements';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { BorderButton, StyledButton } from '@elements/Button';
import {
  CheckboxFormField,
  ControlledTextFormField,
  DropdownFormField,
} from '@elements/FormField';
import { useReturnProcessingCenters } from '@features/storage-providers';
import { DarkPopup } from '@elements/Popup';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import {
  AppTableBody,
  AppTableCell,
  AppTableRoot,
  AppTableRow,
} from '@components/AppTable';

/* The carrier summary is a node, which the string-typed placeholder does not admit. */
const ProviderDropdown = DropdownFormField as ComponentType<
  Omit<DropdownFormFieldProps, 'placeholder'> & { placeholder?: ReactNode }
>;

const CARRIER_ICON = 'sk:size-5 sk:rounded-xs sk:bg-fill-tertiary sk:p-px';

function TextWithEllipsis({ text, maxLength = 30 }) {
  const isTruncated = text?.length > maxLength;
  const displayText = isTruncated ? `${text?.substring(0, maxLength)}…` : text;

  return (
    <>
      <span className="ellipsis-text">{displayText}</span>
      <DarkPopup
        trigger={<Info className="sk:ml-1!" />}
        hoverable
        position="top center"
        className="sk:shrink-0!"
      >
        {text}
      </DarkPopup>
    </>
  );
}

const calculateGirth = (height, width) => (height || 0) * 2 + (width || 0) * 2;

export function CreateReturnLabel({
  orderDetails,
  setShowReturnLabel,
  refreshData,
  refreshShippingLabels,
}) {
  const { footerActive, currentOrgInfo } = useAuth();
  const [generatedLabel, setGenerateLabel] = useState(null);
  const [showPrintLabel, setShowPrintLabel] = useState(false);
  const [saving, setSaving] = useState(null);
  const { data: fpData, loading: fpLoading } = useReturnProcessingCenters(
    orderDetails?.marketplace_region_id,
  );
  const [defaultService, setDefaultService] = useState({});

  const [orderedItems, setOrderedItems] = useState([]);

  useEffect(() => {
    setOrderedItems(orderDetails?.order_contents || []);
  }, [orderDetails]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid: formIsValid, errors },
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(returnSchema),
    shouldUnregister: true,
    defaultValues: {
      // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'order_speed' does not exi...
      order_speed: 'Standard',
    },
  });
  const [fulfillment_provider_id, itemsData] = useWatch({
    name: ['fulfillment_provider_id', 'itemsData'],
    control,
  });

  const checkedItems = useMemo(
    // @ts-expect-error - TS2339: Property 'checked' does not exist on type 'never'.
    () => orderedItems.filter(item => !!item.checked),
    [orderedItems],
  );

  const { data: packagesRates, loading: packagesRateLoading } =
    shippingReturnsRateHook(
      orderDetails?._id,
      fulfillment_provider_id,
      checkedItems,
    );

  const isMultiplePackages = useMemo(
    () =>
      (Object.values(packagesRates?.packages || {}) || []).every(services =>
        Object.values(services || {}).every(service => service.length > 1),
      ) && (Object.values(packagesRates?.packages || {}) || [])?.length > 0,
    [packagesRates],
  );

  useEffect(() => {
    if (
      isMultiplePackages &&
      (Object.values(packagesRates?.packages || {}) || [])?.length > 0
    ) {
      ToastMessage.error(
        'Currently, the system supports only 1 package at a time. Please reduce the quantity or select only one item to proceed.',
      );
    }
  }, [isMultiplePackages]);

  const packagesOptions = useMemo(() => {
    const list = [];
    let i = 0;
    let tempService = {};

    Object.keys(packagesRates?.packages || [])?.map(carrier => {
      const currentFPPackages = packagesRates?.packages;
      const carrierData = currentFPPackages[carrier];
      Object.keys(carrierData || [])?.map(carrierDataItem => {
        let totalRate = 0;

        if (carrierData[carrierDataItem]?.length > 1) {
          return null;
        }

        if (carrierData[carrierDataItem]?.some(item => !!item.error)) {
          return null;
        }
        for (const item of carrierData[carrierDataItem]) {
          if (CommonUtility.isValidNumber(item?.rate)) {
            totalRate += item.rate;
          } else {
            totalRate = -1;
            break;
          }
        }
        if (
          // @ts-expect-error - TS2339: Property 'rate' does not exist on type '{}'.
          (!!tempService?.rate &&
            // @ts-expect-error - TS2339: Property 'rate' does not exist on type '{}'.
            carrierData[carrierDataItem][0]?.rate < tempService?.rate) ||
          // @ts-expect-error - TS2339: Property 'rate' does not exist on type '{}'.
          !tempService?.rate
        ) {
          tempService = carrierData[carrierDataItem][0];
        }
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        list.push({
          ...carrierData[carrierDataItem][0],
          content: (
            <div>
              <div className="sk:flex sk:items-center sk:px-4 sk:py-3">
                <span className="sk:flex sk:w-1/5 sk:items-center">
                  <img
                    alt={carrier}
                    className={cn(CARRIER_ICON, 'sk:mr-1!')}
                    src={carrierServices?.[carrier]?.icon || ''}
                  />
                  {carrierData[carrierDataItem][0]?.provider?.key}
                </span>
                <span className="sk:flex sk:w-[35%] sk:items-center sk:mr-5! ms-2">
                  <Truck className="sk:mr-1! sk:shrink-0!" />{' '}
                  <TextWithEllipsis
                    text={carrierData[carrierDataItem][0]?.service?.key}
                    maxLength={10}
                  />
                </span>
                <span className="sk:flex! sk:items-center! sk:mr-5!">
                  <Calendar className="sk:mr-1!" />{' '}
                  {carrierData[carrierDataItem][0]?.days}d
                </span>
                <span className="sk:ml-auto! sk:flex! sk:items-center!">
                  {totalRate > 0 ? (
                    <>
                      {CommonUtility.currencyFormat(totalRate)}
                      <DarkPopup
                        trigger={<Info className="sk:ml-1!" />}
                        hoverable
                        position="top right"
                        className="sk:shrink-0!"
                      >
                        <div className="sk:[&_table]:bg-transparent! sk:[&_table]:text-white!">
                          <div className="sk:flex! sk:items-center!">
                            <span className="sk:flex sk:w-1/5 sk:items-center">
                              <img
                                alt={carrier}
                                className={cn(CARRIER_ICON, 'sk:mr-1!')}
                                src={carrierServices?.[carrier]?.icon || ''}
                              />
                            </span>
                            {carrierServices?.[carrier]?.text}
                            {' - '}
                            {carrierData[carrierDataItem][0]?.service?.key}
                          </div>
                          <AppTableRoot>
                            <AppTableBody>
                              {carrierData[carrierDataItem]?.map(
                                currentPackage => (
                                  <AppTableRow>
                                    <AppTableCell>
                                      <MuteText className="sk:flex!">
                                        <Package />
                                        &nbsp;
                                        {currentPackage?.length?.toFixed(2)}
                                        &nbsp;x&nbsp;
                                        {currentPackage?.width?.toFixed(2)}
                                        &nbsp;x&nbsp;
                                        {currentPackage.height?.toFixed(2)}
                                        &quot;&nbsp;
                                        {(
                                          (currentPackage?.pounds || 0) +
                                          (currentPackage?.ounces || 0) / 16
                                        ).toFixed(2)}
                                        lbs&nbsp;
                                      </MuteText>
                                    </AppTableCell>
                                    <AppTableCell>
                                      {CommonUtility.currencyFormat(
                                        currentPackage?.rate || '-',
                                      )}
                                    </AppTableCell>
                                  </AppTableRow>
                                ),
                              )}
                              <AppTableRow>
                                <AppTableCell>Total</AppTableCell>
                                <AppTableCell>
                                  {CommonUtility.currencyFormat(
                                    totalRate || '-',
                                  )}
                                </AppTableCell>
                              </AppTableRow>
                            </AppTableBody>
                          </AppTableRoot>
                        </div>
                      </DarkPopup>
                    </>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
            </div>
          ),
          value: {
            service: carrierData[carrierDataItem][0]?.service.value,
            packageValue: carrierData[carrierDataItem][0]?.package.value,
            provider: carrierData[carrierDataItem][0]?.provider.value,
          },
          text: (
            <div className="sk:flex! sk:items-center! sk:justify-between!">
              <span className="sk:flex! sk:items-center!">
                <img
                  alt={carrier}
                  className={cn(CARRIER_ICON, 'sk:mr-1!')}
                  src={carrierServices?.[carrier]?.icon || ''}
                />
                {carrierServices?.[carrier]?.text}
              </span>
              <span className="sk:flex! sk:items-center! sk:mx-auto!">
                <Truck className="sk:mr-1! sk:shrink-0!" />{' '}
                <TextWithEllipsis
                  text={carrierData[carrierDataItem][0]?.service?.key}
                  maxLength={10}
                />
              </span>
              <span className="sk:ml-auto!">
                {totalRate > 0 ? CommonUtility.currencyFormat(totalRate) : '-'}
              </span>
            </div>
          ),
          key: i,
        });
        i += 1;
      });
    });
    setDefaultService(() => tempService);
    return list;
  }, [packagesRates]);

  const printLabelClose = () => {
    setShowReturnLabel(false);
    refreshData();
  };

  useEffect(() => {
    // @ts-expect-error - TS2345: Argument of type '(prevState: never[]) => any[]' is not assignable to parameter ...
    setOrderedItems(prevState =>
      prevState?.map(item => ({
        // @ts-expect-error - TS2698: Spread types may only be created from object types.
        ...item,
        // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
        checked: !!itemsData?.[item._id],
      })),
    );
  }, [itemsData]);

  useEffect(() => {
    setValue(
      'fulfillment_provider_id',
      fpData?.find?.(fp => fp?.item?.is_return_processing_center)?.value ||
        fpData?.[0]?.value,
      { shouldValidate: true },
    );
  }, [fpData]);

  const shippingSpeedData = useMemo(
    () =>
      ['Standard', 'Expedited', 'Priority'].map(item => ({
        value: item,
        text: CommonUtility.toTitleCase(item),
      })),
    [],
  );

  const qtyChanged = (item, quantity) => {
    const temp = orderedItems.map(t => {
      // @ts-expect-error - TS2698: Spread types may only be created from object types.
      const z = { ...t };
      if (item._id === z._id) {
        z.newQty = quantity;
      }
      return z;
    });
    // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
    setOrderedItems(temp);
  };

  const onSubmit = async data => {
    try {
      if (checkedItems.length === 0) {
        ToastMessage.error('Select items to create shipment label');
        return;
      }
      const testMode = currentOrgInfo?.test && !CommonUtility.isProductionEnv();
      // @ts-expect-error - TS2345: Argument of type '"Generating Label"' is not assignable to parameter of type 'Se...
      setSaving('Generating Label');

      let service = null;

      if (CommonUtility.isValidObject(data?.packagesData)) {
        for (const pack of Object.values(packagesRates?.packages)) {
          // @ts-expect-error - TS2769: No overload matches this call.
          for (const provider of Object.values(pack)) {
            // @ts-expect-error - TS18046: 'provider' is of type 'unknown'.
            service = provider.find(
              x =>
                x.package.value === data?.packagesData?.packageValue &&
                x.service.value === data?.packagesData?.service &&
                x.provider.value === data?.packagesData?.provider,
            );
            if (service) {
              break;
            }
          }
          if (service) {
            break;
          }
        }
      } else {
        // @ts-expect-error - TS2322: Type '{}' is not assignable to type 'null'.
        service = defaultService;
      }

      const reqData = {
        fulfillment_provider_id: data.fulfillment_provider_id,
        packing_list: true,
        mode: testMode ? 'test' : 'production',
        order_id: orderDetails._id,
        force: true,
        order_speed: 'Standard',
        metadata: {
          returns: true,
        },
        items: checkedItems.map(item => ({
          // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
          item_id: item?._id,
          // @ts-expect-error - TS2339: Property 'newQty' does not exist on type 'never'.
          qty: item?.newQty || item?.content?.qty,
        })),
      };

      if (data?.destination?.name) {
        // @ts-expect-error - TS2339: Property 'destination' does not exist on type '{ fulfillment_provider_id: any; p...
        reqData.destination = {
          name: data?.destination?.name,
        };
      }

      if (CommonUtility.isValidObject(data?.packagesData)) {
        // @ts-expect-error - TS2339: Property 'packages' does not exist on type '{ fulfillment_provider_id: any; pack...
        reqData.packages = [
          {
            package: data?.packagesData?.packageValue,
            service: data?.packagesData?.service,
            provider: data?.packagesData?.provider,
            // @ts-expect-error - TS18047: 'service' is possibly 'null'.
            pounds: service.pounds,
            // @ts-expect-error - TS18047: 'service' is possibly 'null'.
            ounces: service.ounces,
            // @ts-expect-error - TS18047: 'service' is possibly 'null'.
            length: service.length,
            // @ts-expect-error - TS18047: 'service' is possibly 'null'.
            width: service.width,
            // @ts-expect-error - TS18047: 'service' is possibly 'null'.
            height: service.height,
            // @ts-expect-error - TS18047: 'service' is possibly 'null'.
            girth: calculateGirth(service.height, service.width),
          },
        ];
      }

      const result = await ShippingLabelService.generateReturnLabel(reqData);
      setGenerateLabel({
        testMode,
        ...result.data,
      });
      setShowPrintLabel(true);
    } catch (error) {
      ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(null);
      refreshShippingLabels();
    }
  };

  return (
    <div className="sk:bg-surface">
      {saving && <LoaderBar content={saving || 'Loading'} />}
      {/* @ts-expect-error - TS2554: Expected 1 arguments, but got 2. */}
      <form noValidate onSubmit={handleSubmit(data => onSubmit(data, false))}>
        <div
          className={cn(
            'sk:mx-auto sk:my-4 sk:overflow-y-auto sk:pb-[170px]',
            footerActive
              ? 'sk:h-[calc(100vh-120px)]'
              : 'sk:h-[calc(100vh-60px)]',
          )}
        >
          <PageHeaderText className="sk:flex! sk:items-center! sk:justify-between!">
            <span>Create Return Label</span>
          </PageHeaderText>
          <Accordion
            className="sk:my-4"
            title={
              <div className="sk:flex! sk:items-center!">
                <User className="sk:mr-1! sk:text-fg-tertiary" /> Origin{' '}
                <MuteText className="sk:ml-1!">(Customer Address)</MuteText>
              </div>
            }
          >
            <div className="sk:pl-4">
              <div>
                <MuteText>Ship from</MuteText>
                {orderDetails?.customer_details?.address_line_one ? (
                  <span>
                    <br />
                    {orderDetails?.customer_details?.address_line_one}, <br />
                  </span>
                ) : (
                  ''
                )}
                {orderDetails?.customer_details?.address_line_two ? (
                  <span>
                    {orderDetails?.customer_details?.address_line_two},{' '}
                  </span>
                ) : (
                  ''
                )}
                {orderDetails?.customer_details?.city ? (
                  <span>
                    {orderDetails?.customer_details?.city}, <br />
                  </span>
                ) : (
                  ''
                )}
                {orderDetails?.customer_details?.state ? (
                  <span>{orderDetails?.customer_details?.state},</span>
                ) : (
                  ''
                )}
                {orderDetails?.customer_details?.zip ? (
                  <span>{orderDetails?.customer_details?.zip},</span>
                ) : (
                  ''
                )}
                {orderDetails?.customer_details?.country ? (
                  <span>
                    {orderDetails?.customer_details?.country},<br />
                  </span>
                ) : (
                  ''
                )}
                {orderDetails?.customer_details?.phone ? (
                  <span>{orderDetails?.customer_details?.phone}</span>
                ) : (
                  ''
                )}
              </div>
            </div>
          </Accordion>
          <DropdownFormField
            required
            name="fulfillment_provider_id"
            placeholder="Destination"
            options={fpData || []}
            loading={fpLoading}
            disabled={fpLoading}
            label={<MuteText>Destination</MuteText>}
            sorted={false}
            control={control}
          />
          <ControlledTextFormField
            label="Destination Name ( optional )"
            name="destination.name"
            hint={errors?.destination?.name?.message}
            error={errors?.destination?.name}
            control={control}
            required={false}
            placeholder="Your Destination Name"
            maxLength={150}
          />
          <DropdownFormField
            required
            name="order_speed"
            placeholder="Service"
            options={shippingSpeedData || []}
            disabled
            label={<MuteText>Service</MuteText>}
            sorted={false}
            control={control}
          />
          <ProviderDropdown
            name="packagesData"
            loading={packagesRateLoading}
            disabled={packagesRateLoading}
            placeholder={
              // @ts-expect-error - TS2339: Property 'provider' does not exist on type '{}'.
              defaultService?.provider ? (
                <div className="sk:flex! sk:items-center! sk:justify-between!">
                  <span className="sk:flex! sk:items-center!">
                    <img
                      // @ts-expect-error - TS2339: Property 'provider' does not exist on type '{}'.
                      alt={defaultService?.provider?.value}
                      className={cn(CARRIER_ICON, 'sk:mr-1!')}
                      src={
                        carrierServices?.[
                          // @ts-expect-error - TS2339: Property 'provider' does not exist on type '{}'.
                          defaultService?.provider?.key
                            ?.toString()
                            ?.toLowerCase()
                        ]?.icon || ''
                      }
                    />
                    {/* @ts-expect-error - TS2339: Property 'provider' does not exist on type '{}'. */}
                    {carrierServices?.[defaultService?.provider?.value]?.text}
                  </span>
                  <span className="sk:flex! sk:items-center! sk:mx-auto!">
                    <Truck className="sk:mr-1! sk:shrink-0!" />{' '}
                    <TextWithEllipsis
                      // @ts-expect-error - TS2339: Property 'service' does not exist on type '{}'.
                      text={defaultService?.service?.key}
                      maxLength={10}
                    />
                  </span>
                  <span className="sk:ml-auto!">
                    {/* @ts-expect-error - TS2339: Property 'rate' does not exist on type '{}'. */}
                    {CommonUtility.currencyFormat(defaultService.rate)}
                  </span>
                </div>
              ) : (
                'Carrier'
              )
            }
            options={packagesOptions || []}
            label={<MuteText>Carrier</MuteText>}
            sorted={false}
            control={control}
          />
          <div>
            {orderedItems?.map(item => (
              /* @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'. */
              <Fragment key={item._id}>
                <div
                  // @ts-expect-error - TS2322: Type '{ children: Element[]; disabled: boolean; className: string; }' is not ass...
                  disabled={!item.available}
                  className="sk:flex! sk:items-center!"
                >
                  <CheckboxFormField
                    className="sk:mr-2!"
                    control={control}
                    // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
                    name={`itemsData.[${item._id}]`}
                    defaultValue
                  />
                  {/* @ts-expect-error - TS2741: Property 'price' is missing in type '{ item: never; canChang... */}
                  <ProductRow
                    item={item}
                    canChangeQty
                    qtyChanged={qty => qtyChanged(item, qty)}
                    // @ts-expect-error - TS2339: Property 'newQty' does not exist on type 'never'.
                    qty={item?.newQty || item?.content?.qty}
                  />
                </div>
              </Fragment>
            ))}
          </div>
          <div className="sk:absolute sk:bottom-0 sk:left-0 sk:z-100 sk:w-full sk:border-t sk:border-border sk:bg-surface sk:px-5 sk:py-4">
            <div className="sk:my-2! sk:flex!">
              <BorderButton
                type="button"
                className="sk:ml-auto!"
                onClick={() => setShowReturnLabel(false)}
              >
                Cancel
              </BorderButton>
              <StyledButton
                disabled={
                  fpLoading ||
                  isMultiplePackages ||
                  packagesRateLoading ||
                  !formIsValid ||
                  saving !== null
                }
                type="submit"
              >
                Create Label
              </StyledButton>
            </div>
          </div>
        </div>
      </form>
      <PrintLabelPopup
        open={showPrintLabel}
        closeModal={printLabelClose}
        // @ts-expect-error - TS2322: Type '{ open: boolean; closeModal: () => void; url: any; name: any; printURL: an...
        url={generatedLabel?.url}
        // @ts-expect-error - TS2339: Property 'key' does not exist on type 'never'.
        name={generatedLabel?.key}
        // @ts-expect-error - TS2339: Property 'url' does not exist on type 'never'.
        printURL={generatedLabel?.url}
      />
    </div>
  );
}

function ProductRow({
  item,
  qty,
  price,
  canChangeQty,
  qtyChanged,
  hideImage = false,
}) {
  const qtyNos = item?.content?.qty;
  const title =
    item.g_attributes?.short_title || item?.p_title || item?.item_name;

  const [popupOpen, setPopupOpen] = useState(false);
  const closePopup = () => {
    setPopupOpen(false);
  };

  return (
    <div className="sk:flex! sk:items-center!">
      <div className="sk:size-[30px]">
        {!hideImage && <ItemImage item={item} />}
      </div>
      <div className="sk:ml-2.5 sk:flex sk:w-[calc(100%-40px)] sk:flex-col">
        <p className="sk:mb-0!" title={title}>
          <strong>
            <TextCopyToClipboard text={title} iconSize={16} />
          </strong>
        </p>
        {item?.p_sku && <div className="sk:my-2!">SKU : {item.p_sku}</div>}
        {(qtyNos || qty) && (
          <div className="sk:flex! align-items">
            <div className={`sk:mb-2! ${item?.p_sku ? '' : 'sk:mt-2!'}`}>
              Qty : <BoldText>{qty || qtyNos}x</BoldText>
            </div>
            {canChangeQty && (
              <Popover
                placement="top"
                aria-label="Change shipping quantity"
                isOpen={popupOpen}
                onOpenChange={setPopupOpen}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="sk:ml-2 sk:underline"
                  >
                    Change
                  </Button>
                }
              >
                <ChangeQuantity
                  qty={item.newQty || item?.content?.qty}
                  maxQty={item?.content?.qty}
                  qtyChanged={qtyChanged}
                  closePopup={closePopup}
                />
              </Popover>
            )}
          </div>
        )}
        {price && (
          <div>
            Product Total :{' '}
            <BoldText>{CommonUtility.currencyFormat(price)}</BoldText>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangeQuantity({ qty, maxQty, qtyChanged, closePopup }) {
  const [newQty, setNewQty] = useState(qty || '');
  return (
    <div className="sk:min-h-[100px] sk:px-[0.9rem] sk:py-4">
      <div>
        <BoldText>Shipping Quantity</BoldText>
      </div>
      <div className="sk:my-3.5!">
        <label>Quantity</label>
        <div>
          <NumberMaskInput
            maxValue={maxQty}
            minValue={1}
            value={newQty}
            onChange={setNewQty}
          />
        </div>
        <div>
          <MuteText>Max: {CommonUtility.numberWithCommas(maxQty)}</MuteText>
        </div>
      </div>
      <div>
        <StyledButton
          fluid
          type="button"
          disabled={!newQty}
          onClick={() => {
            qtyChanged(+newQty);
            closePopup();
          }}
        >
          Confirm
        </StyledButton>
      </div>
    </div>
  );
}
